import './index.css';
import { IndicatorSingleValue } from '../../../../screens/ProfitCalculations/types';
import { DomainTuple } from 'victory';
import { useMemo } from 'react';

type DataPoint = {
  low: number;
  open: number;
  close: number;
  high: number;
  x: number;
  x2: number;
};

interface Props {
  movingAverages: {
    data: IndicatorSingleValue[];
    color: string;
    label: string;
  }[];
  domain:
    | {
        x: DomainTuple;
        y: DomainTuple;
      }
    | undefined;
  candles: DataPoint[];
}

const MovingAverageChartLegend = (props: Props) => {
  const { movingAverages, domain, candles } = props;

  const position = useMemo(() => {
    if (!domain) {
      return {
        top: '60px',
        left: '60px',
      };
    }

    /** We want to position the legend on a poart of the chart that has no candles on it */
    const yRatio = 1 / 5;
    const xRatio = 1 / 13;

    const domainYRange = (domain.y[1] as number) - (domain.y[0] as number);
    const domainXRange = (domain.x[1] as number) - (domain.x[0] as number);

    /** TOP LEFT */
    const topLeftArea = {
      y: [
        (domain.y[1] as number) - domainYRange * yRatio,
        domain.y[1] as number,
      ],
      x: [
        domain.x[0] as number,
        (domain.x[0] as number) + domainXRange * xRatio,
      ],
    };

    const leftCandles = candles.filter((c) => c.x < topLeftArea.x[1]);

    const maxValueTopLeft = Math.max(
      ...leftCandles.map((c) => Math.max(c.close, c.open)),
    );

    if (maxValueTopLeft < topLeftArea.y[0]) {
      /** We use default values */
      return {
        top: '60px',
        left: '60px',
      };
    }

    /** TOP RIGHT */
    const topRightArea = {
      y: [
        (domain.y[1] as number) - domainYRange * yRatio,
        domain.y[1] as number,
      ],
      x: [
        (domain.x[1] as number) - domainXRange * xRatio,
        domain.x[1] as number,
      ],
    };

    const rightCandles = candles.filter((c) => c.x >= topRightArea.x[0]);

    const maxValueTopRight = Math.max(
      ...rightCandles.map((c) => Math.max(c.close, c.open)),
    );

    if (maxValueTopRight < topRightArea.y[0]) {
      /** We use default values */
      return {
        top: '60px',
        right: '100px',
      };
    }

    /** BOTTOM LEFT */
    const bottomLeftArea = {
      y: [
        domain.y[0] as number,
        (domain.y[0] as number) + domainYRange * yRatio,
      ],
      x: [
        domain.x[0] as number,
        (domain.x[0] as number) + domainXRange * xRatio,
      ],
    };

    const minValueBottomLeft = Math.min(
      ...leftCandles.map((c) => Math.min(c.close, c.open)),
    );

    if (minValueBottomLeft > bottomLeftArea.y[1]) {
      /** We use default values */
      return {
        bottom: '60px',
        left: '60px',
      };
    }

    /** BOTTOM RIGHT */
    const bottomRightArea = {
      y: [
        domain.y[0] as number,
        (domain.y[0] as number) + domainYRange * yRatio,
      ],
      x: [
        (domain.x[1] as number) - domainXRange * xRatio,
        domain.x[1] as number,
      ],
    };

    const minValueBottomRight = Math.min(
      ...rightCandles.map((c) => Math.min(c.close, c.open)),
    );

    if (minValueBottomRight > bottomRightArea.y[1]) {
      /** We use default values */
      return {
        bottom: '60px',
        right: '100px',
      };
    }

    /** Default */
    return {
      top: '60px',
      left: '60px',
    };
  }, [candles, domain]);

  return (
    <div className="candlesticks-chart-legend-root" style={{ ...position }}>
      {movingAverages.map((ma) => (
        <div className="candlesticks-chart-legend-row" key={ma.label}>
          <div
            className="candlesticks-chart-legend-color"
            style={{ backgroundColor: ma.color }}
          ></div>
          <span className="candlesticks-chart-legend-text">{ma.label}</span>
        </div>
      ))}
    </div>
  );
};

export default MovingAverageChartLegend;
